import { useState, useEffect } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { Trophy, Plus, Edit2, Trash2, Users, Shuffle } from "lucide-react";

interface Competition {
  id: number;
  title: string;
  prizeAmountPence: number;
  entryFeePence: number;
  startDate: string;
  endDate: string;
  drawDate: string;
  enabled: boolean;
  status: string;
  winnerId: number | null;
  winnerName: string | null;
  winnerAnnounced: boolean;
  termsUrl: string | null;
  entryCount: number;
}

function pnd(p: number) { return `£${(p / 100).toFixed(2)}`; }
function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function toLocalInput(iso: string) {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const STATUS_COLORS: Record<string, string> = {
  upcoming: "bg-blue-100 text-blue-700",
  active:   "bg-green-100 text-green-700",
  drawn:    "bg-purple-100 text-purple-700",
  cancelled:"bg-red-100 text-red-700",
};

const token = () => localStorage.getItem("ayrshire_token") ?? "";

export default function AdminCompetitions() {
  const { toast } = useToast();
  const [competitions, setCompetitions] = useState<Competition[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Competition | null>(null);
  const [saving, setSaving] = useState(false);
  const [drawingId, setDrawingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const blankForm = {
    title: "Weekly Cash Draw",
    prizeAmountPounds: "50",
    entryFeePence: "100",
    startDate: "",
    endDate: "",
    drawDate: "",
    enabled: false,
    termsUrl: "",
  };
  const [form, setForm] = useState(blankForm);

  async function load() {
    try {
      const r = await fetch("/api/admin/competitions", { headers: { Authorization: `Bearer ${token()}` } });
      const d = await r.json() as { competitions: Competition[] };
      setCompetitions(d.competitions ?? []);
    } catch { toast({ title: "Failed to load competitions", variant: "destructive" }); }
    finally { setLoading(false); }
  }

  useEffect(() => { void load(); }, []);

  function openCreate() {
    setEditing(null);
    setForm(blankForm);
    setShowForm(true);
  }

  function openEdit(c: Competition) {
    setEditing(c);
    setForm({
      title: c.title,
      prizeAmountPounds: (c.prizeAmountPence / 100).toFixed(0),
      entryFeePence: String(c.entryFeePence),
      startDate: toLocalInput(c.startDate),
      endDate: toLocalInput(c.endDate),
      drawDate: toLocalInput(c.drawDate),
      enabled: c.enabled,
      termsUrl: c.termsUrl ?? "",
    });
    setShowForm(true);
  }

  async function handleSave() {
    if (!form.startDate || !form.endDate || !form.drawDate) {
      toast({ title: "Please fill in all date fields", variant: "destructive" }); return;
    }
    setSaving(true);
    try {
      const body = {
        title: form.title,
        prizeAmountPence: Math.round(Number(form.prizeAmountPounds) * 100),
        entryFeePence: Number(form.entryFeePence),
        startDate: new Date(form.startDate).toISOString(),
        endDate: new Date(form.endDate).toISOString(),
        drawDate: new Date(form.drawDate).toISOString(),
        enabled: form.enabled,
        termsUrl: form.termsUrl || null,
      };
      const url = editing ? `/api/admin/competitions/${editing.id}` : "/api/admin/competitions";
      const method = editing ? "PATCH" : "POST";
      const r = await fetch(url, {
        method, headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error("Save failed");
      toast({ title: editing ? "Competition updated" : "Competition created" });
      setShowForm(false);
      await load();
    } catch { toast({ title: "Failed to save competition", variant: "destructive" }); }
    finally { setSaving(false); }
  }

  async function toggleEnabled(c: Competition) {
    try {
      await fetch(`/api/admin/competitions/${c.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ enabled: !c.enabled }),
      });
      await load();
    } catch { toast({ title: "Failed to update", variant: "destructive" }); }
  }

  async function handleDraw(c: Competition) {
    if (!confirm(`Draw a winner for "${c.title}"? This cannot be undone.`)) return;
    setDrawingId(c.id);
    try {
      const r = await fetch(`/api/admin/competitions/${c.id}/draw`, {
        method: "POST", headers: { Authorization: `Bearer ${token()}` },
      });
      const d = await r.json() as { winner?: { name: string } };
      toast({ title: `Winner drawn: ${d.winner?.name ?? "Unknown"}! Push notification sent.` });
      await load();
    } catch { toast({ title: "Draw failed", variant: "destructive" }); }
    finally { setDrawingId(null); }
  }

  async function handleDelete(c: Competition) {
    if (!confirm(`Delete "${c.title}" and all its entries?`)) return;
    setDeletingId(c.id);
    try {
      await fetch(`/api/admin/competitions/${c.id}`, {
        method: "DELETE", headers: { Authorization: `Bearer ${token()}` },
      });
      await load();
    } catch { toast({ title: "Delete failed", variant: "destructive" }); }
    finally { setDeletingId(null); }
  }

  return (
    <AdminLayout current="/admin/competitions">
      <div className="max-w-5xl mx-auto py-8 px-4 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2"><Trophy className="w-6 h-6 text-amber-500" /> Cash Competitions</h1>
            <p className="text-slate-500 text-sm mt-1">Manage weekly cash draw competitions. UK law requires a free entry route — available via /competition on the website.</p>
          </div>
          <Button onClick={openCreate} className="flex items-center gap-2">
            <Plus className="w-4 h-4" /> New Competition
          </Button>
        </div>

        {loading ? (
          <p className="text-slate-500">Loading…</p>
        ) : competitions.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-slate-500">No competitions yet. Create one above.</CardContent></Card>
        ) : (
          <div className="space-y-4">
            {competitions.map((c) => (
              <Card key={c.id} className={c.enabled ? "border-amber-300" : ""}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <CardTitle className="text-base font-bold">{c.title}</CardTitle>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${STATUS_COLORS[c.status] ?? "bg-slate-100 text-slate-600"}`}>
                          {c.status.charAt(0).toUpperCase() + c.status.slice(1)}
                        </span>
                        {c.enabled && <Badge className="bg-green-100 text-green-700 border-0 text-xs">Live</Badge>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch checked={c.enabled} onCheckedChange={() => void toggleEnabled(c)} />
                      <Button size="sm" variant="outline" onClick={() => openEdit(c)}><Edit2 className="w-3.5 h-3.5" /></Button>
                      {c.status !== "drawn" && (
                        <Button size="sm" variant="outline" className="text-purple-600 border-purple-200 hover:bg-purple-50"
                          onClick={() => void handleDraw(c)} disabled={drawingId === c.id}>
                          <Shuffle className="w-3.5 h-3.5 mr-1" />{drawingId === c.id ? "Drawing…" : "Draw Winner"}
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700"
                        onClick={() => void handleDelete(c)} disabled={deletingId === c.id}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                    <div><p className="text-slate-500 text-xs">Prize</p><p className="font-bold text-lg text-amber-600">{pnd(c.prizeAmountPence)}</p></div>
                    <div><p className="text-slate-500 text-xs">Entry Fee</p><p className="font-semibold">{pnd(c.entryFeePence)}</p></div>
                    <div><p className="text-slate-500 text-xs">Entries</p><p className="font-semibold flex items-center gap-1"><Users className="w-3.5 h-3.5 text-slate-400" />{c.entryCount}</p></div>
                    <div><p className="text-slate-500 text-xs">Draw Date</p><p className="font-semibold">{fmtDate(c.drawDate)}</p></div>
                  </div>
                  <div className="mt-3 text-xs text-slate-500">
                    {fmtDate(c.startDate)} → {fmtDate(c.endDate)}
                  </div>
                  {c.status === "drawn" && c.winnerName && (
                    <div className="mt-3 flex items-center gap-2 bg-purple-50 border border-purple-200 rounded-lg px-3 py-2 text-sm">
                      <Trophy className="w-4 h-4 text-purple-500" />
                      <span className="text-purple-700 font-semibold">Winner: {c.winnerName}</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Competition" : "New Competition"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Title</Label>
              <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Prize Amount (£)</Label>
                <Input type="number" min="1" value={form.prizeAmountPounds}
                  onChange={e => setForm(f => ({ ...f, prizeAmountPounds: e.target.value }))} />
              </div>
              <div>
                <Label>Entry Fee (pence)</Label>
                <Input type="number" min="1" value={form.entryFeePence}
                  onChange={e => setForm(f => ({ ...f, entryFeePence: e.target.value }))} />
                <p className="text-xs text-slate-400 mt-0.5">e.g. 100 = £1.00</p>
              </div>
            </div>
            <div>
              <Label>Start Date & Time</Label>
              <Input type="datetime-local" value={form.startDate}
                onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))} />
            </div>
            <div>
              <Label>End Date & Time</Label>
              <Input type="datetime-local" value={form.endDate}
                onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))} />
            </div>
            <div>
              <Label>Draw Date & Time</Label>
              <Input type="datetime-local" value={form.drawDate}
                onChange={e => setForm(f => ({ ...f, drawDate: e.target.value }))} />
            </div>
            <div>
              <Label>Terms URL (optional)</Label>
              <Input placeholder="https://…" value={form.termsUrl}
                onChange={e => setForm(f => ({ ...f, termsUrl: e.target.value }))} />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.enabled} onCheckedChange={v => setForm(f => ({ ...f, enabled: v }))} />
              <Label>Enabled (live to users)</Label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button onClick={() => void handleSave()} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
